import { useEffect, useState } from 'react';
import { Link, Navigate, useOutletContext } from 'react-router-dom';
import { useVersionedApi } from '../utils/versionedApi';
import { safeStorage } from '../utils/safeStorage';
import { useAuth } from '../AuthContext';
import { useIntegratedSidebar } from '../hooks/useIntegratedSidebar';
import standaloneBundleNames from '../../../shared/standaloneBundles.json';

// Owning a bundle is a tenant entitlement. Opening that bundle's documents also
// needs the user's own program access, the same access_* permission that decides
// which programs the sidebar shows, otherwise a Behavioral Health practitioner
// in an organization that owns Adult Day Services could open every ADS window.
// Bundles without a program of their own keep the tenant-level check only.
const PROGRAM_ACCESS_BY_BUNDLE = {
  'adult-day-services': 'access_adult_day',
  'behavioral-health': 'access_behavioral_health',
  'home-health': 'access_home_health',
  // Same permission the sidebar's canAccessProgram and the API's
  // requireProgramAccess('residential-services') use.
  'residential-services': 'view_residential',
  '245d-waiver-services': 'view_245d_basic',
  // Same permission as requireProgramAccess('case-management') on the API.
  'case-management': 'view_case_management',
  // requireProgramAccess('housing-support') on the API.
  'housing-support': 'view_housing_support',
};
const PROGRAM_ACCESS_BYPASS_ROLES = new Set(['admin', 'super_admin']);
// The server answered the permission request: a set (possibly empty) or a
// 403/404 meaning this user has none. Anything else is "unknown".
const ANSWERED_PERMISSION_STATUSES = new Set(['loaded', 'denied']);

export default function BundleProtectedRoute({
  children,
  requiredBundle,
  fallbackPath = '/admin',
}) {
  const outletContext = useOutletContext();
  const {
    bundlesLoaded = false,
    bundlesLoadFailed = false,
    orgBundles = [],
  } = outletContext || {};
  const apiClient = useVersionedApi('v1');
  const { user, authChecked } = useAuth();
  const {
    hasProgramAccess,
    loading: permissionsLoading,
    error: permissionsError,
    permissions,
    permissionsStatus,
  } = useIntegratedSidebar();
  const [externalBundles, setExternalBundles] = useState([]);
  const [standaloneLoaded, setStandaloneLoaded] = useState(Boolean(outletContext));
  const [standaloneLoadFailed, setStandaloneLoadFailed] = useState(false);

  // Full-screen document windows live outside AdminShell and therefore do not
  // receive its outlet context. Resolve the same tenant entitlement directly
  // instead of leaving those windows unprotected or permanently loading.
  useEffect(() => {
    if (outletContext) return undefined;
    // Several window routes sit outside ProtectedRoute, so this can mount for a
    // cached user with no token. Wait for the session check; with no session,
    // hold no bundles (the fallback route then sends them to sign in) rather
    // than sending a request that can only 400 NO_ORGANIZATION_CONTEXT.
    if (authChecked === false) return undefined;
    if (!user?.organizationId || !safeStorage.getItem('evv_token')) {
      setExternalBundles([]);
      setStandaloneLoaded(true);
      return undefined;
    }
    let cancelled = false;
    apiClient.get('/bundles/my-bundles')
      .then((response) => {
        if (cancelled) return;
        setExternalBundles(response.data?.bundles || []);
        setStandaloneLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setStandaloneLoadFailed(true);
          setStandaloneLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [apiClient, outletContext, authChecked, user?.organizationId]);

  const resolvedBundles = outletContext ? orgBundles : externalBundles;
  const required = Array.isArray(requiredBundle) ? requiredBundle : [requiredBundle];
  const bypassesProgramAccess = PROGRAM_ACCESS_BYPASS_ROLES.has(user?.role);
  const needsProgramAccess = !bypassesProgramAccess
    && required.some((slug) => PROGRAM_ACCESS_BY_BUNDLE[slug]);
  const noPermissions = Object.keys(permissions || {}).length === 0;
  const permissionsAnswered = ANSWERED_PERMISSION_STATUSES.has(permissionsStatus);
  // An empty set the server answered is "no access" (QA M1, 2026-10-20: staff
  // with no 245D permissions saw "Could not verify feature access"). Only a
  // request with no answer is "could not verify". Neither mounts the page.
  const permissionsUnavailable = needsProgramAccess
    && !permissionsLoading
    && (Boolean(permissionsError) || (noPermissions && !permissionsAnswered));
  const holdsNoPermissions = needsProgramAccess
    && !permissionsLoading
    && !permissionsError
    && noPermissions
    && permissionsAnswered;

  const resolvedLoaded = (outletContext ? bundlesLoaded : standaloneLoaded)
    && !(needsProgramAccess && permissionsLoading);
  const resolvedLoadFailed = (outletContext ? bundlesLoadFailed : standaloneLoadFailed)
    || permissionsUnavailable;

  if (!resolvedLoaded) {
    return (
      <div role="status" aria-label="Loading" className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
      </div>
    );
  }

  // Do not mount protected pages (and their background requests) on an unknown
  // entitlement. A temporary discovery outage is recoverable, not a grant.
  if (resolvedLoadFailed) return (
    <div role="alert" className="flex min-h-64 items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
      <div className="max-w-sm text-center">
        <p className="font-medium text-gray-900 dark:text-white">Could not verify feature access</p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Reload the page to try again.</p>
        <button onClick={() => window.location.reload()} className="mt-4 rounded-lg bg-teal-600 px-4 py-2 text-white hover:bg-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500">Reload</button>
      </div>
    </div>
  );

  const held = new Set(resolvedBundles.map((item) => item?.bundle?.slug || item?.slug).filter(Boolean));
  const standalone = new Set(standaloneBundleNames);
  const tenantHolds = (slug) => held.has(slug) || (held.has('complete-care') && !standalone.has(slug));
  // Server's program-access decision (permission, program assignment or
  // provider type, exactly as the API checks), else the program's permission.
  const userMayUse = (slug) => bypassesProgramAccess
    || !PROGRAM_ACCESS_BY_BUNDLE[slug]
    || hasProgramAccess(slug);
  const tenantOwnsRequired = required.some((slug) => typeof slug === 'string' && slug.length > 0 && tenantHolds(slug));

  // The organization has the program; this person has no permissions at all.
  // Say so plainly instead of bouncing them or suggesting a reload.
  if (holdsNoPermissions) {
    if (!tenantOwnsRequired) return <Navigate to={fallbackPath} replace />;
    return (
      <div role="alert" className="flex min-h-64 items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
        <div className="max-w-sm text-center">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">You don&apos;t have access to this program</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Your account has no permissions for it. If you need it for your work, ask an administrator to update your role.</p>
          <Link to={fallbackPath} className="mt-4 inline-flex min-h-[44px] items-center rounded-lg bg-teal-600 px-4 py-2 text-white hover:bg-teal-700 focus-visible:ring-2 focus-visible:ring-teal-500">Go to your dashboard</Link>
        </div>
      </div>
    );
  }

  const hasAccess = required.some((slug) => typeof slug === 'string' && slug.length > 0 && tenantHolds(slug) && userMayUse(slug));

  if (!hasAccess) return <Navigate to={fallbackPath} replace />;

  return children;
}
